import { useEffect, useMemo, useRef, useState } from "react";
import type { EvidenceItem } from "../domain/contracts";
import { fetchMediaObjectUrlForEvidence } from "../services/media/mediaManifestService";

type UseProtectedMediaOptions = {
  enabled?: boolean;
  limit?: number;
};

export function useProtectedMedia(evidence: EvidenceItem[], options: UseProtectedMediaOptions = {}) {
  const { enabled = true, limit = 6 } = options;
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(new Map());
  const objectUrlsRef = useRef(objectUrls);
  const limitedEvidence = useMemo(() => evidence.slice(0, limit), [evidence, limit]);
  const evidenceKey = limitedEvidence.map((item) => `${item.assetId}:${item.mediaType}`).join("|");

  useEffect(() => {
    objectUrlsRef.current = objectUrls;
  }, [objectUrls]);

  useEffect(() => {
    let disposed = false;

    async function load() {
      const missingEvidence = limitedEvidence.filter((item) => !objectUrlsRef.current.has(item.assetId));
      if (missingEvidence.length === 0) {
        return;
      }

      const nextEntries = await Promise.all(
        missingEvidence.map(async (item) => {
          try {
            const objectUrl = await fetchMediaObjectUrlForEvidence(item);
            return [item.assetId, objectUrl] as const;
          } catch {
            return null;
          }
        }),
      );

      if (!disposed) {
        setObjectUrls((current) => {
          const nextMap = new Map(current);
          for (const entry of nextEntries) {
            if (entry) {
              nextMap.set(entry[0], entry[1]);
            }
          }
          if (mapsEqual(current, nextMap)) {
            return current;
          }
          return nextMap;
        });
      } else {
        for (const entry of nextEntries) {
          if (entry?.[1]) {
            URL.revokeObjectURL(entry[1]);
          }
        }
      }
    }

    if (enabled && limitedEvidence.length > 0) {
      void load();
    }

    return () => {
      disposed = true;
    };
  }, [enabled, evidenceKey, limitedEvidence]);

  useEffect(() => () => {
    for (const objectUrl of objectUrlsRef.current.values()) {
      URL.revokeObjectURL(objectUrl);
    }
  }, []);

  return useMemo(() => {
    const nextMap = new Map<string, string>();
    for (const item of limitedEvidence) {
      const objectUrl = objectUrls.get(item.assetId);
      if (objectUrl) {
        nextMap.set(item.assetId, objectUrl);
      }
    }
    return nextMap;
  }, [limitedEvidence, objectUrls]);
}

function mapsEqual(left: Map<string, string>, right: Map<string, string>): boolean {
  if (left.size !== right.size) {
    return false;
  }

  for (const [key, value] of left.entries()) {
    if (right.get(key) !== value) {
      return false;
    }
  }

  return true;
}